import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Space, message, Alert, Divider, Descriptions, Tag } from 'antd';
import { SaveOutlined, SendOutlined, EditOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { getNotifyConfig, saveNotifyConfig, testNotify } from '../../api/config';
import { hasPerm } from '../../hooks/usePermission';

const PROVIDER_OPTIONS = [
    { value: 'qq', label: 'QQ 邮箱' },
    { value: '163', label: '163 邮箱' },
];

const SMTP_MAP = {
    qq: { smtp_host: 'smtp.qq.com', smtp_port: 465 },
    '163': { smtp_host: 'smtp.163.com', smtp_port: 465 },
};

const EMAIL_RE = /^[\w.-]+@(qq\.com|163\.com)$/;

const providerLabel = (v) => (PROVIDER_OPTIONS.find((p) => p.value === v) || {}).label || v;
const sslLabel = (v) => (v ? 'SSL 加密' : '不加密');

const NotifyConfig = () => {
    const [form] = Form.useForm();
    const [config, setConfig] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const provider = Form.useWatch('provider', form);

    const fetchConfig = async () => {
        try {
            const res = await getNotifyConfig();
            if (res.code === 0) {
                setConfig(res.data);
                form.setFieldsValue(res.data);
            } else {
                message.error(res.message || '获取配置失败');
            }
        } catch (error) {
            console.error('Fetch notify config failed:', error);
        }
    };

    useEffect(() => {
        fetchConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (editing && provider && SMTP_MAP[provider]) {
            form.setFieldsValue(SMTP_MAP[provider]);
        }
    }, [provider, editing, form]);

    const startEdit = () => {
        form.setFieldsValue(config || {});
        setEditing(true);
    };

    const handleCancel = () => {
        form.setFieldsValue(config || {});
        setEditing(false);
    };

    const handleSave = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (e) {
            return;
        }
        setSaving(true);
        try {
            const res = await saveNotifyConfig(values);
            if (res.code === 0) {
                message.success('保存成功');
                setConfig(values);
                setEditing(false);
            } else {
                message.error(res.message || '保存失败');
            }
        } catch (error) {
            console.error('Save notify config failed:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (e) {
            return;
        }
        setTesting(true);
        try {
            const res = await testNotify(values);
            if (res.code === 0) {
                message.success(res.message || '测试邮件发送成功');
            } else {
                message.error(res.message || '测试发送失败');
            }
        } catch (error) {
            console.error('Test notify failed:', error);
        } finally {
            setTesting(false);
        }
    };

    const recipientField = (listName, label) => (
        <Form.Item label={label}>
            <Form.List name={listName}>
                {(fields, { add, remove }) => (
                    <div>
                        {fields.map(({ key, name }) => (
                            <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                                <Form.Item
                                    name={name}
                                    style={{ marginBottom: 0 }}
                                    rules={[
                                        { required: true, message: '请输入收件人邮箱' },
                                        { pattern: EMAIL_RE, message: '仅支持 QQ 邮箱 / 163 邮箱' },
                                    ]}
                                >
                                    <Input placeholder="收件人邮箱，如 alarm@qq.com" style={{ width: 280 }} />
                                </Form.Item>
                                <MinusCircleOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} onClick={() => remove(name)} />
                            </Space>
                        ))}
                        <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
                            添加收件人
                        </Button>
                    </div>
                )}
            </Form.List>
        </Form.Item>
    );

    const renderRecipients = (list) => {
        if (!list || !list.length) return '-';
        return (
            <Space wrap size={4}>
                {list.map((email) => <Tag key={email} color="blue">{email}</Tag>)}
            </Space>
        );
    };

    return (
        <div style={{ padding: '16px' }}>
            <Card
                title="邮箱告警通知"
                style={{ maxWidth: 760 }}
                extra={
                    !editing && hasPerm('config:notify:edit') && (
                        <Button type="primary" icon={<EditOutlined />} onClick={startEdit}>编辑</Button>
                    )
                }
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="系统异常告警（重复授权请求 / CPUID 绑定冲突）将按此配置发送通知邮件；仅支持 QQ 邮箱 / 163 邮箱"
                />

                {!editing && config && (
                    <>
                        <Divider orientation="left" style={{ marginTop: 0 }}>SMTP 服务器配置</Divider>
                        <Descriptions column={2} size="small">
                            <Descriptions.Item label="邮箱服务商">{providerLabel(config.provider)}</Descriptions.Item>
                            <Descriptions.Item label="SMTP 服务器">{config.smtp_host}</Descriptions.Item>
                            <Descriptions.Item label="端口">{config.smtp_port}</Descriptions.Item>
                            <Descriptions.Item label="加密方式">{sslLabel(config.ssl)}</Descriptions.Item>
                            <Descriptions.Item label="授权码">******</Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left">发件人管理</Divider>
                        <Descriptions column={2} size="small">
                            <Descriptions.Item label="发件邮箱">{config.sender_email}</Descriptions.Item>
                            <Descriptions.Item label="发件人名称">{config.sender_name || '-'}</Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left">告警收件人配置</Divider>
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="重复授权请求">{renderRecipients(config.repeat_recipients)}</Descriptions.Item>
                            <Descriptions.Item label="CPUID绑定冲突">{renderRecipients(config.conflict_recipients)}</Descriptions.Item>
                        </Descriptions>
                    </>
                )}

                {editing && (
                    <Form form={form} layout="vertical">
                        <Divider orientation="left" style={{ marginTop: 0 }}>SMTP 服务器配置</Divider>

                        <Form.Item name="provider" label="邮箱服务商" rules={[{ required: true, message: '请选择邮箱服务商' }]}>
                            <Select placeholder="请选择" options={PROVIDER_OPTIONS} />
                        </Form.Item>

                        <Form.Item name="smtp_host" label="SMTP 服务器">
                            <Input disabled />
                        </Form.Item>

                        <Form.Item name="smtp_port" label="端口">
                            <Input disabled />
                        </Form.Item>

                        <Form.Item name="ssl" label="加密方式" initialValue={true}>
                            <Select options={[{ value: true, label: 'SSL 加密' }, { value: false, label: '不加密' }]} />
                        </Form.Item>

                        <Form.Item name="auth_code" label="授权码" rules={[{ required: true, message: '请输入授权码' }]}>
                            <Input.Password placeholder="QQ/163 邮箱的 SMTP 授权码" />
                        </Form.Item>

                        <Divider orientation="left">发件人管理</Divider>

                        <Form.Item
                            name="sender_email"
                            label="发件邮箱"
                            rules={[
                                { required: true, message: '请输入发件邮箱' },
                                { pattern: EMAIL_RE, message: '仅支持 QQ 邮箱 / 163 邮箱' },
                            ]}
                        >
                            <Input placeholder="如 no-reply@qq.com" />
                        </Form.Item>

                        <Form.Item name="sender_name" label="发件人名称">
                            <Input placeholder="如 易莱孚" />
                        </Form.Item>

                        <Divider orientation="left">告警收件人配置</Divider>

                        {recipientField('repeat_recipients', '重复授权请求 收件人')}
                        {recipientField('conflict_recipients', 'CPUID绑定冲突 收件人')}

                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                {hasPerm('config:notify:test') && (
                                    <Button icon={<SendOutlined />} loading={testing} onClick={handleTest}>测试发送</Button>
                                )}
                                {hasPerm('config:notify:save') && (
                                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>保存</Button>
                                )}
                                <Button onClick={handleCancel}>取消</Button>
                            </Space>
                        </Form.Item>
                    </Form>
                )}
            </Card>
        </div>
    );
};

export default NotifyConfig;
